// Página de "Meu perfil" — qualquer usuário autenticado pode editar os
// próprios dados (cargo, foto, telefone comercial, CREA, LinkedIn).
// Reusa o endpoint PATCH /admin/usuarios/{email} que já permite self-edit
// (backend valida que o email no path == email do token quando não é admin).
//
// Foi criada porque colaboradores como o Gustavo (grupo 'comercial', sem admin)
// não viam o módulo Admin no menu — então não tinham onde editar o próprio
// perfil/foto/CREA usados nas propostas.

import { useEffect, useState } from 'react';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  Card, Button, Input, Form, Row, Col, Space, Typography,
  App as AntApp, Flex, Spin, Alert,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { API_URL } from '../config';

const { Title, Text } = Typography;

async function getToken(): Promise<string> {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString() ?? '';
}

async function getMeuEmail(): Promise<string> {
  const session = await fetchAuthSession();
  const payload: any = session.tokens?.idToken?.payload || {};
  return (payload.email || '').toLowerCase();
}

interface PerfilDados {
  email: string;
  nome: string;
  telefone?: string;
  cargo?: string;
  foto_url?: string;
  telefone_comercial?: string;
  crea?: string;
  crea_rnp?: string;
  crea_titulo?: string;
  linkedin?: string;
}

export default function MeuPerfil() {
  const { message } = AntApp.useApp();
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [original, setOriginal] = useState<PerfilDados | null>(null);

  const [nome, setNome] = useState('');
  const [telefone, setTelefone] = useState('');
  const [cargo, setCargo] = useState('');
  const [fotoUrl, setFotoUrl] = useState('');
  const [telComercial, setTelComercial] = useState('');
  const [crea, setCrea] = useState('');
  const [creaRnp, setCreaRnp] = useState('');
  const [creaTitulo, setCreaTitulo] = useState('');
  const [linkedin, setLinkedin] = useState('');

  useEffect(() => { carregar(); }, []);

  async function carregar() {
    setCarregando(true);
    setErro(null);
    try {
      const [token, meuEmail] = await Promise.all([getToken(), getMeuEmail()]);
      if (!meuEmail) {
        setErro('Não foi possível identificar seu email. Faça login novamente.');
        return;
      }
      // Endpoint já permite leitura para qualquer autenticado
      const r = await axios.get(`${API_URL}/admin/usuarios`, { headers: { Authorization: token } });
      const lista: PerfilDados[] = r.data.body ? JSON.parse(r.data.body) : r.data;
      const meu = lista.find((u: any) => (u.email || '').toLowerCase() === meuEmail);
      if (!meu) {
        setErro(`Usuário "${meuEmail}" não encontrado no Cognito.`);
        return;
      }
      setOriginal(meu);
      setNome(meu.nome || '');
      setTelefone(meu.telefone || '');
      setCargo(meu.cargo || '');
      setFotoUrl(meu.foto_url || '');
      setTelComercial(meu.telefone_comercial || '');
      setCrea(meu.crea || '');
      setCreaRnp(meu.crea_rnp || '');
      setCreaTitulo(meu.crea_titulo || '');
      setLinkedin(meu.linkedin || '');
    } catch (e: any) {
      setErro('Erro ao carregar perfil: ' + (e.response?.data?.erro || e.message));
    } finally { setCarregando(false); }
  }

  async function salvar() {
    if (!original) return;
    if (!nome.trim()) { message.warning('Nome obrigatório'); return; }
    setSalvando(true);
    try {
      const token = await getToken();
      const payload: any = {};
      if (nome.trim() !== (original.nome || '').trim()) payload.nome = nome.trim();
      if (telefone !== (original.telefone || '')) payload.telefone = telefone;
      if (cargo !== (original.cargo || '')) payload.cargo = cargo;
      if (fotoUrl !== (original.foto_url || '')) {
        if (fotoUrl.startsWith('data:')) payload.foto_base64 = fotoUrl;
        else payload.foto_url = fotoUrl;
      }
      if (telComercial !== (original.telefone_comercial || '')) payload.telefone_comercial = telComercial;
      if (crea !== (original.crea || '')) payload.crea = crea;
      if (creaRnp !== (original.crea_rnp || '')) payload.crea_rnp = creaRnp;
      if (creaTitulo !== (original.crea_titulo || '')) payload.crea_titulo = creaTitulo;
      if (linkedin !== (original.linkedin || '')) payload.linkedin = linkedin;
      if (Object.keys(payload).length === 0) {
        message.info('Nenhuma alteração para salvar.');
        return;
      }
      await axios.patch(`${API_URL}/admin/usuarios/${encodeURIComponent(original.email)}`,
        payload, { headers: { Authorization: token } });
      message.success('Perfil atualizado.');
      await carregar();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.erro || e.message));
    } finally { setSalvando(false); }
  }

  if (carregando) {
    return <div style={{ padding: 40, textAlign: 'center' }}><Spin tip="Carregando perfil..." /></div>;
  }
  if (erro) {
    return <div style={{ padding: 24 }}><Alert type="error" message={erro} showIcon /></div>;
  }

  return (
    <div style={{ padding: 16, maxWidth: 900 }}>
      <Title level={4} style={{ color: '#020D7F', marginBottom: 4 }}>Meu perfil</Title>
      <Text type="secondary" style={{ fontSize: 12 }}>
        Edite seus dados pessoais. Estes dados aparecem nas propostas que você gera,
        ARTs assinadas e cabeçalhos de documentos.
      </Text>

      <Card size="small" title="🛍 Perfil pessoal" style={{ marginTop: 14 }}>
        <Form layout="vertical" size="small">
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item label="Nome completo" required>
                <Input value={nome} onChange={e => setNome(e.target.value)} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Email (login)">
                <Input value={original?.email || ''} disabled />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Cargo">
                <Input value={cargo} onChange={e => setCargo(e.target.value)}
                  placeholder="Ex.: Engenheiro Civil Pleno" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Telefone pessoal (Cognito)">
                <Input value={telefone} onChange={e => setTelefone(e.target.value)}
                  placeholder="+5551999999999" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Telefone comercial (mostrado em propostas)">
                <Input value={telComercial} onChange={e => setTelComercial(e.target.value)}
                  placeholder="(51) 9 9999-9999" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="LinkedIn">
                <Input value={linkedin} onChange={e => setLinkedin(e.target.value)}
                  placeholder="https://linkedin.com/in/seuperfil" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="Foto (avatar, máx 4 MB)">
                <FotoUploader fotoAtual={fotoUrl} onChange={setFotoUrl} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>

      <Card size="small" title="🪪 Cadastro técnico (CREA — emissão de ART)" style={{ marginTop: 14 }}>
        <Form layout="vertical" size="small">
          <Row gutter={12}>
            <Col xs={24} md={8}>
              <Form.Item label="Carteira CREA">
                <Input value={crea} onChange={e => setCrea(e.target.value)} placeholder="Ex.: RS214787" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="RNP">
                <Input value={creaRnp} onChange={e => setCreaRnp(e.target.value)} placeholder="Ex.: 2215003898" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Título profissional">
                <Input value={creaTitulo} onChange={e => setCreaTitulo(e.target.value)}
                  placeholder="Ex.: Engenheiro Civil" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>

      <Flex justify="flex-end" gap={10} style={{ marginTop: 16 }}>
        <Button onClick={() => carregar()}>Descartar alterações</Button>
        <Button type="primary" onClick={salvar} loading={salvando}>Salvar</Button>
      </Flex>
    </div>
  );
}

function FotoUploader({ fotoAtual, onChange }: { fotoAtual: string; onChange: (url: string) => void }) {
  const { message } = AntApp.useApp();
  const [preview, setPreview] = useState<string>(fotoAtual);
  const [carregando, setCarregando] = useState(false);
  useEffect(() => { setPreview(fotoAtual); }, [fotoAtual]);

  function lerArquivo(file: File) {
    if (file.size > 4 * 1024 * 1024) { message.error('Foto excede 4 MB.'); return; }
    setCarregando(true);
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result);
      setPreview(dataUrl); onChange(dataUrl); setCarregando(false);
    };
    reader.onerror = () => { message.error('Erro ao ler arquivo'); setCarregando(false); };
    reader.readAsDataURL(file);
  }

  return (
    <Flex align="center" gap={14}>
      {preview
        ? <img src={preview} alt="" style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover', border: '1px solid #ddd' }} />
        : <div style={{ width: 80, height: 80, borderRadius: '50%', background: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#aaa', fontSize: 28 }}>?</div>}
      <Space direction="vertical" size={4}>
        <Button type="primary" ghost size="small" icon={<UploadOutlined />} loading={carregando}>
          <label style={{ cursor: 'pointer' }}>
            {preview ? 'Trocar foto' : 'Escolher foto'}
            <input type="file" accept="image/png,image/jpeg,image/webp" style={{ display: 'none' }}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) lerArquivo(f); }} />
          </label>
        </Button>
        {preview && (
          <Button type="link" size="small" danger onClick={() => { setPreview(''); onChange(''); }}>
            🗑 Remover
          </Button>
        )}
      </Space>
    </Flex>
  );
}
